/**
 * Settlement detection.
 *
 * Identifies bank debits that are credit card bill payments, not real expenses.
 * E.g., HDFC savings shows "AMEX PAYMENT ₹45,000" — that's a settlement for
 * the Amex credit card, not an actual ₹45,000 expense.
 *
 * Environment-agnostic: runs in WebWorker or server.
 */

import type { AccountLink, ExistingTransaction, Settlement } from "@paisa/types"
import { normaliseName } from "./normalise"

/**
 * Patterns that indicate CC bill payments in bank narrations.
 *
 * Must be precise — "HDFC ERGO INSURANCE" and "SBI MUTUAL FUND" must NOT match.
 * Each pattern requires both a CC issuer/platform keyword AND a payment-related keyword.
 */
const SETTLEMENT_PATTERNS: RegExp[] = [
  // CRED bill payments (most common in Indian banking)
  /\bCRED\b.*\bPAYMENT\b/i,
  /\bCRED\.CLUB\b/i,

  // Amex — require PAYMENT or BILL context, or known Amex payment UPI patterns
  /\bAMEX\b.*\bPAYMENT\b/i,
  /\bAMERICAN EXPRESS\b.*\b(?:PAYMENT|BILL|CR)\b/i,
  /\bAMEXCC\b/i, // BillDesk pattern: "BILLDESKPG.AMEXCC@HDFCBANK"
  /\bAEBC\d{15,}/i, // Amex UPI reference pattern

  // Generic CC payment phrases
  /\bCC\s*PAYMENT\b/i,
  /\bCREDIT\s*CARD\s*(?:PAYMENT|BILL)\b/i,
  /\bCARD\s*BILL\s*PAYMENT\b/i,

  // Bank-specific CC keywords — require "CC" adjacent to bank name
  /\bHDFC\s*CC\b/i,
  /\bHDFCBK\s*CC\b/i,
  /\bICICI\s*CC\b/i,
  /\bAXIS\s*CC\b/i,
  /\bSBI\s*CARD\s*(?:PAYMENT|BILL)\b/i,
  /\bHSBC\s*(?:CC|CREDIT\s*CARD)\b/i,
  /\bKOTAK\s*CC\b/i,
  /\bRBL\s*(?:CC|CREDIT\s*CARD)\b/i,
  /\bINDUSIND\s*CC\b/i,
  /\bYES\s*BANK\s*CC\b/i,
  /\bONECARD\b.*\bPAYMENT\b/i,

  // VISA/Mastercard bill pay (rare but possible)
  /\bVISA\s*BILL\b/i,
  /\bMASTERCARD\b.*\bPAYMENT\b/i,

  // NACH/Standing instruction for CC
  /\bNACH\b.*\b(?:CC|CREDIT\s*CARD)\b/i,
]

// ── Brand normalisation ──────────────────────────────────────────────────────

/**
 * Map a human-readable bank name to the canonical brand key used in enrichedData.
 * Matches the brands produced by extractSettlementCardInfo().
 *
 * Used server-side to look up settlement candidates when a CC account is linked:
 * e.g. bankAccount.bankName "American Express" → "amex"
 */
export function normaliseBrand(bankName: string): string {
  const s = bankName.toLowerCase().trim()
  if (s.includes("american express") || s === "amex") return "amex"
  if (s.startsWith("hdfc")) return "hdfc"
  if (s.startsWith("icici")) return "icici"
  if (s.startsWith("axis")) return "axis"
  if (s.startsWith("sbi")) return "sbi"
  if (s.startsWith("kotak")) return "kotak"
  if (s.startsWith("hsbc")) return "hsbc"
  if (s.startsWith("rbl")) return "rbl"
  if (s.startsWith("indusind")) return "indusind"
  if (s === "onecard") return "onecard"
  if (s.startsWith("yes bank")) return "yesbank"
  return s.replace(/\s+/g, "")
}

// ── Card brand extraction ────────────────────────────────────────────────────

/**
 * Maps regex patterns to canonical CC issuer IDs.
 *
 * Deliberately excludes payment platforms (CRED, BillDesk) — they don't tell
 * us which card was paid, so brand stays null for those.
 */
const CARD_BRAND_PATTERNS: Array<[RegExp, string]> = [
  // AEBC is Amex's UPI/NACH payment routing code — exclusively used for Amex bill payments
  [/\bAEBC\d{10,}/i, "amex"],
  [/\bAMEX\b|\bAMERICAN\s+EXPRESS\b|\bAMEXCC\b/i, "amex"],
  [/\bHDFC\s*CC\b|\bHDFCBK\s*CC\b/i, "hdfc"],
  [/\bICICI\s*CC\b/i, "icici"],
  [/\bAXIS\s*CC\b/i, "axis"],
  [/\bSBI\s*CARD\b/i, "sbi"],
  [/\bKOTAK\s*CC\b/i, "kotak"],
  [/\bHSBC\s*(?:CC|CREDIT\s*CARD)\b/i, "hsbc"],
  [/\bRBL\s*(?:CC|CREDIT\s*CARD)\b/i, "rbl"],
  [/\bINDUSIND\s*CC\b/i, "indusind"],
  [/\bONECARD\b/i, "onecard"],
  [/\bYES\s*BANK\s*CC\b/i, "yesbank"],
]

/**
 * After a card-related keyword, look for exactly 4 digits (optionally preceded
 * by masking chars like X or *). `(?!\d)` prevents matching 5-digit amounts.
 * `\b` after keyword prevents "AXISB" matching as "AXIS".
 */
const LAST4_PATTERN =
  /(?:CARD|CC|CREDIT|AMEX|EXPRESS|HDFC|ICICI|AXIS|SBI|KOTAK|ONECARD)\b[\s\-/*]*(?:X{2,}|\*{2,})?\s*(\d{4})(?!\d)/i

/**
 * Extract the CC brand and last 4 card digits from a bank settlement narration.
 *
 * Only meaningful when called after isSettlementDescription() confirms a CC payment.
 * Both fields are stored in enrichedData on the transaction so the correct CC
 * account can be auto-linked retroactively when the user adds it later.
 *
 * brand: canonical issuer id ("amex", "hdfc", …) or null (CRED/platform/generic)
 * last4: 4-digit card suffix from the narration, or null if not present
 */
export function extractSettlementCardInfo(description: string): {
  brand: string | null
  last4: string | null
} {
  let brand: string | null = null
  for (const [pattern, b] of CARD_BRAND_PATTERNS) {
    if (pattern.test(description)) {
      brand = b
      break
    }
  }

  const m = LAST4_PATTERN.exec(description)
  const last4 = m ? m[1] : null

  return { brand, last4 }
}

// ── Settlement pattern detection ─────────────────────────────────────────────

/**
 * Detect whether a transaction description looks like a credit card settlement.
 *
 * Returns the matched pattern for debugging, or null if not a settlement.
 */
export function isSettlementDescription(description: string): string | null {
  for (const pattern of SETTLEMENT_PATTERNS) {
    if (pattern.test(description)) {
      return pattern.source
    }
  }
  return null
}

/** Settlement-card fields stored on a transaction's `enrichedData` for later CC retro-linking. */
export interface SettlementEnrichedData {
  settlementCardBrand?: string
  settlementCardLast4?: string
}

/**
 * Build the `enrichedData` payload for a bank-statement debit that looks like a
 * credit-card settlement, or null when it doesn't apply.
 *
 * IMPORTANT: this reads the description text, so it MUST run on PLAINTEXT. In
 * private mode the description is encrypted client-side before it reaches the
 * server, so the importing client (MCP tool / web Vault flow) must call this
 * BEFORE encrypting and pass the result through — the server can't derive it
 * from ciphertext (see #77).
 */
export function settlementEnrichedData(
  description: string,
  type: "debit" | "credit",
  source: string,
): SettlementEnrichedData | null {
  if (type !== "debit" || source !== "bank_statement") return null
  if (!isSettlementDescription(description)) return null
  const { brand, last4 } = extractSettlementCardInfo(description)
  if (brand === null && last4 === null) return null
  const out: SettlementEnrichedData = {}
  if (brand) out.settlementCardBrand = brand
  if (last4) out.settlementCardLast4 = last4
  return out
}

/**
 * Detect settlements in a set of existing transactions.
 *
 * A settlement is a bank debit that pays off a linked credit card.
 * Uses both the configured account links (explicit) and narration pattern
 * matching (heuristic).
 */
export function detectSettlements(
  transactions: ExistingTransaction[],
  accountLinks: AccountLink[],
): Settlement[] {
  const settlements: Settlement[] = []

  // Only look at debits — settlements are outgoing payments from bank accounts
  const debits = transactions.filter(
    (t) => t.type === "debit" && !t.isDuplicate && !t.isIgnored && !t.isTransfer,
  )

  for (const debit of debits) {
    // Check 1: Does the narration match known settlement patterns?
    const patternMatch = isSettlementDescription(debit.description)
    if (!patternMatch) continue

    // Check 2: Can we identify which credit card it settles?
    const linkedAccount = findLinkedCreditCard(debit, accountLinks)
    const { brand, last4 } = extractSettlementCardInfo(debit.description)

    settlements.push({
      bankDebit: debit,
      creditAccountId: linkedAccount?.creditAccountId ?? "unknown",
      statementTotal: null, // will be filled when CC statement is available
      reason: linkedAccount
        ? `Settlement for ${linkedAccount.creditAccountId} (keyword: ${linkedAccount.settlementKeyword})`
        : `Likely CC settlement (pattern: ${patternMatch})`,
      cardBrand: brand,
      cardLast4: last4,
    })
  }

  return settlements
}

/**
 * Find which credit card account a bank debit is settling.
 *
 * Checks account links — if the debit account is linked to a CC and the
 * narration contains the settlement keyword, it's a match.
 */
function findLinkedCreditCard(
  debit: ExistingTransaction,
  accountLinks: AccountLink[],
): AccountLink | null {
  if (!debit.accountId) return null

  for (const link of accountLinks) {
    if (link.debitAccountId !== debit.accountId) continue

    // If there's a keyword hint, check for it in the narration
    if (link.settlementKeyword) {
      const normDesc = normaliseName(debit.description)
      if (normDesc.includes(normaliseName(link.settlementKeyword))) {
        return link
      }
    } else {
      // No keyword — any settlement-pattern match from this debit account counts
      return link
    }
  }

  return null
}
